import type { CouponDiscountKind, CurrencyCode, ScratchPrizePromotionFragment } from '#graphql-operations'
import type { Ref } from 'vue'
import { confirmScratchCode } from '~/composables/useScratchSubmit'

/**
 * Campaign coupon carried in a URL — `?code=FB30` on a product page, a
 * collection, the homepage, anywhere.
 *
 * The code is validated against the shop API (`couponPreview`) before anything
 * is shown or applied, then kept for the rest of the browser session so the
 * whole journey is consistent: every product surface prices with the coupon
 * already deducted, the cart gets the code the moment it has a line, and
 * checkout opens with it applied. Only a plain order-percentage promotion
 * yields a per-product price — see `discountPercent` in the shop schema — so
 * `percent` is null for fixed-amount codes, which are still applied to the cart
 * but shown nowhere on the product surfaces.
 *
 * Not every product is discounted: a gift card is stored value, bought and spent
 * at face value, and the promotion engine steps around it (see
 * `promotions/gift-card-lines` in the Vendure app). Which products those are is
 * not guessed here — `couponPreview` names the facet values, and `couponExcludes`
 * below is how every surface asks.
 */

const COOKIE_NAME = 'promo.code'
/**
 * A personal scratch-card prize code gets its OWN carrier slot. It discounts
 * nothing (the gift rule adds a gift once the cart qualifies), so it must never
 * reach the percentage-pricing surfaces below — and a later campaign link must
 * not be able to overwrite a prize that is still waiting for a qualifying bag.
 */
const PRIZE_COOKIE_NAME = 'prize.code'
/**
 * The verdict on the code in `prize.code`, in its own slot beside it and with
 * exactly its lifetime and flags — it describes that code and is meaningless
 * without it, so it must die with it.
 *
 * `<code>|<ruleId>|<expiry epoch ms>|<confirmed 1/0>`: everything a held prize
 * needs that is not the gift copy. Without it every FULL page load a code holder
 * made paid for a `couponPreview`, and that query rebuilds the whole prize
 * server-side (~24 statements, the design-family scan included) to answer a
 * question this browser already had answered. Epoch ms rather than the ISO
 * string the API returns because the cookie rides every same-origin request and
 * nothing ever does anything with `expiresAt` but parse it. The last field says
 * the mailbox confirmation for this code got through (`confirmScratchCode`), so
 * later loads stop sending it; a three-field verdict written before it existed
 * reads as not yet confirmed and costs one more call.
 *
 * Parsed defensively, the TYPE included: Nuxt's cookie decoder runs
 * `JSON.parse` over what it reads, so a `prize.ok=123` set by hand or by a
 * sibling subdomain arrives as a number and `.split` on it would throw — out of
 * `restore()`, which the plugin only logs, leaving the prize unrestored for the
 * rest of the session. Anything unrecognised simply means "no verdict", which
 * costs one preview — what every load used to cost.
 */
const PRIZE_VERDICT_COOKIE_NAME = 'prize.ok'
/**
 * The gift copy the cart banner words a held prize with, keyed by gift rule id.
 * One record: a browser holds at most one prize code (`scratchStore` enforces
 * the same — one code is all anyone gets).
 *
 * Deliberately NOT in the cookie: the promotion carries the promo image, the
 * landing collection and every gift and condition product with their assets —
 * kilobytes against a 4 KB cookie that would then be re-sent on every
 * same-origin request, GraphQL calls and images included. It is rule content,
 * identical for every visitor of the channel (`activeGiftPromotions` serves
 * this very shape from the CDN for an hour), so unlike the code it is neither
 * personal nor a bearer credential and may sit in web storage — which is what
 * keeps `scratchStore`'s rule that no prize code is ever written there true:
 * the code stays in the cookie, only the words live here.
 */
const PRIZE_PROMOTION_KEY = 'scratch-card:prize:v1'
/**
 * How stale that copy may get. It changes only when someone edits the rule, the
 * surface it comes from is itself edge-cached for an hour, and it decides
 * neither money nor validity — so a day-old headline is the same class of
 * staleness the PDP's own gift block already accepts.
 */
const PRIZE_PROMOTION_TTL_MS = 24 * 60 * 60 * 1000

export interface UrlCoupon {
  code: string
  percent: number | null
  /** Fixed amount or percentage: it only competes with a code of its own kind on the order. */
  kind: CouponDiscountKind | null
  /**
   * FacetValue ids the discount does not apply to — gift cards. Matched by id
   * because that is the one form every surface has: the search index returns
   * bare `facetValueIds`, a product or an order line returns `facetValues`
   * objects, and both carry the same ids.
   */
  excludedFacetValueIds: string[]
}

export type CouponPrizeStatusValue = 'LIVE' | 'REDEEMED' | 'EXPIRED' | 'UNAVAILABLE'
/** The prize a code unlocks, in the `activeGiftPromotions` shape `giftCopy` words. */
export type PrizePromotion = ScratchPrizePromotionFragment

export interface UrlPrize {
  code: string
  ruleId: string
  /** Exclusive: the start of the day after the last valid day, in Europe/Prague. */
  expiresAt: string
  promotion: PrizePromotion | null
}

export type AdoptOutcome
  = | { kind: 'none' }
    | { kind: 'coupon', valid: boolean }
    | { kind: 'prize', status: CouponPrizeStatusValue | 'INVALID' }

/**
 * Shared by the coupon plugin and the prize swap: at most one coupon write in
 * flight. Applying a code means removing another one first, and two writers
 * interleaving those steps can leave an order with neither.
 */
export const couponWriteLock = { busy: false }

/** What the shop API says about a code, without adopting it. */
export interface CouponPreviewResult {
  /** The canonical code as the promotion stores it. */
  code: string
  /** Redeemable right now: exists, is enabled, is in date and has uses left. */
  valid: boolean
  /** Percentage off the whole order, or null when the code is not a plain percentage one. */
  percent: number | null
  /** Fixed amount or percentage — an order holds one code of each — or null for any other promotion. */
  kind: CouponDiscountKind | null
  /**
   * Set for every personal scratch-card prize code of this channel, whatever
   * its status — so an expired or spent prize can be named as one instead of
   * being reported as an unknown code. Null for every other coupon.
   */
  prize: {
    ruleId: string
    status: CouponPrizeStatusValue
    expiresAt: string
    promotion: PrizePromotion | null
  } | null
  /** FacetValue ids the discount steps around — see `UrlCoupon.excludedFacetValueIds`. */
  excludedFacetValueIds: string[]
}

/**
 * A product as any of the storefront's surfaces describes its facet values: the
 * search index gives bare ids, everything else gives objects carrying one.
 * Both are accepted so a caller can hand over whatever it already has.
 */
export interface CouponFacetValueSource {
  facetValueIds?: readonly (string | number)[] | null
  facetValues?: readonly ({ id?: string | number | null } | null)[] | null
}

/**
 * Previews in flight, keyed by the normalised code. The apply watcher weighs
 * every code on the order against the campaign one, and the prize path does the
 * same, so the identical lookup can be asked for several times in one tick.
 */
const previewsInFlight = new Map<string, Promise<CouponPreviewResult>>()

/**
 * Asks the shop API whether `code` can be redeemed right now and how deep it is.
 * A standalone function rather than part of the composable's state because it is
 * also how a code already sitting on an order gets weighed against a campaign
 * one — see the coupon watcher in `plugins/3.urlCoupon`.
 */
export async function previewCoupon(code: string): Promise<CouponPreviewResult> {
  const normalized = code.trim().toUpperCase()
  // Browser only. The map is module-scoped, and since the plugin's validation
  // half runs during SSR too, sharing it on the server would hand one render's
  // answer to another request for the same code — a different channel, and for
  // a prize code a different customer, whose `valid`/`status` verdict is their
  // own. A server render asks for a code once anyway; the de-duplication is
  // there for the browser, where the apply watcher and the prize path can ask
  // for the same code in one tick.
  const pending = import.meta.client ? previewsInFlight.get(normalized) : undefined
  if (pending)
    return pending

  const request = (async (): Promise<CouponPreviewResult> => {
    const { data } = await useGraphqlQuery('couponPreview', { couponCode: normalized })
    const preview = data.couponPreview

    return {
      code: preview?.couponCode ?? normalized,
      valid: Boolean(preview?.valid),
      percent: preview?.discountPercent ?? null,
      kind: preview?.discountKind ?? null,
      excludedFacetValueIds: (preview?.excludedFacetValueIds ?? []).map(String),
      prize: preview?.prize
        ? {
            ruleId: String(preview.prize.ruleId),
            status: preview.prize.status,
            expiresAt: String(preview.prize.expiresAt),
            promotion: preview.prize.promotion ?? null,
          }
        : null,
    }
  })().finally(() => previewsInFlight.delete(normalized))

  if (import.meta.client)
    previewsInFlight.set(normalized, request)
  return request
}

/**
 * Whether the campaign coupon leaves this product alone — a gift card, today.
 *
 * A source that carries no facet values at all cannot be recognised and is
 * treated as discounted, which is the safe way round: it prices the product the
 * way the cart will, and the surfaces that can show a gift card all select the
 * facet values they need.
 */
export function couponExcludes(
  coupon: UrlCoupon | null,
  source?: CouponFacetValueSource | null,
): boolean {
  const excluded = coupon?.excludedFacetValueIds
  if (!excluded?.length || !source)
    return false

  const ids = source.facetValueIds ?? source.facetValues?.map(value => value?.id) ?? []
  return ids.some(id => id != null && excluded.includes(String(id)))
}

/**
 * The `promo.code` cookie, as ONE ref for the whole app instance.
 *
 * `useCookie` hands back a fresh ref per call and registers a fresh writer for
 * it, and every writer fires at the end of a server render with whatever its own
 * ref holds. Two refs for one cookie therefore race: this composable is called
 * from every product tile, so a ref created after the plugin adopted a code
 * still holds the `null` it read from the request headers and would write that
 * over the adopted code — Nuxt warns about exactly this ("was previously set to
 * … and is being overridden to null"). Memoising on the Nuxt instance keeps it
 * to one ref per request, and per browser session on the client.
 */
function storedCodeCookie(): Ref<string | null> {
  const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & { _promoCodeCookie?: Ref<string | null> }
  // Survives reloads, the navigation away from the URL that carried the code,
  // and follows the customer into other tabs — but no further: a session
  // cookie (no maxAge/expires) dies when the browser fully closes, so the
  // campaign price never greets them days later without the campaign link.
  return (nuxtApp._promoCodeCookie ??= useCookie<string | null>(COOKIE_NAME, {
    default: () => null,
    sameSite: 'lax',
  }))
}

/**
 * The prize carrier, memoised for the same reason as the campaign one above.
 * Same lifetime, separate slot: a prize code the customer has been e-mailed
 * survives the navigation away from the link but not the browser closing.
 */
function storedPrizeCodeCookie(): Ref<string | null> {
  const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & { _prizeCodeCookie?: Ref<string | null> }
  return (nuxtApp._prizeCodeCookie ??= useCookie<string | null>(PRIZE_COOKIE_NAME, {
    default: () => null,
    sameSite: 'lax',
  }))
}

/** The verdict slot, memoised and given the code cookie's own options. */
function storedPrizeVerdictCookie(): Ref<string | null> {
  const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & { _prizeVerdictCookie?: Ref<string | null> }
  return (nuxtApp._prizeVerdictCookie ??= useCookie<string | null>(PRIZE_VERDICT_COOKIE_NAME, {
    default: () => null,
    sameSite: 'lax',
  }))
}

/** What `prize.ok` carries — see `PRIZE_VERDICT_COOKIE_NAME`. */
interface StoredPrizeVerdict {
  code: string
  ruleId: string
  /** `UrlPrize.expiresAt` as epoch ms. */
  expiresAt: number
  /** The mailbox confirmation for this code got through; see `PRIZE_VERDICT_COOKIE_NAME`. */
  confirmed: boolean
}

/** `unknown`, not `string`: the ref is typed `string | null`, the decoder is not. */
function parsePrizeVerdict(raw: unknown): StoredPrizeVerdict | null {
  const [code, ruleId, expiresAt, confirmed] = (typeof raw === 'string' ? raw : '').split('|')
  const at = Number(expiresAt)
  if (!code || !ruleId || !Number.isFinite(at) || at <= 0)
    return null
  return { code, ruleId, expiresAt: at, confirmed: confirmed === '1' }
}

function formatPrizeVerdict(verdict: StoredPrizeVerdict): string {
  return [verdict.code, verdict.ruleId, verdict.expiresAt, verdict.confirmed ? '1' : '0'].join('|')
}

interface StoredPrizePromotion {
  v: 1
  ruleId: string
  /**
   * The language the copy was fetched in — and with it the market: each
   * storefront locale is its own Vendure channel, so a record written for one
   * can never word a banner in another.
   */
  locale: string
  at: number
  promotion: PrizePromotion | null
}

/**
 * The cached copy for this rule, or null when there is none to trust. Wrapped
 * rather than returned bare because a rule with no promotion to show is a HIT
 * that restores to `null` — the generic banner title, exactly what the preview
 * answered — not a miss worth a round trip.
 */
function readPrizePromotion(ruleId: string, locale: string, now: number): { promotion: PrizePromotion | null } | null {
  if (!import.meta.client)
    return null
  try {
    const raw = window.localStorage.getItem(PRIZE_PROMOTION_KEY)
    const stored = raw ? JSON.parse(raw) as Partial<StoredPrizePromotion> | null : null
    if (!stored || stored.v !== 1 || stored.ruleId !== ruleId || stored.locale !== locale || stored.promotion === undefined)
      return null
    const at = typeof stored.at === 'number' ? stored.at : 0
    if (at <= 0 || now - at > PRIZE_PROMOTION_TTL_MS)
      return null
    return { promotion: stored.promotion }
  }
  catch {
    // Unreadable or unparseable — hardened profiles and Safari's private mode
    // throw on `localStorage`. The restore falls back to the preview, which is
    // what every load did before this cache existed.
    return null
  }
}

function writePrizePromotion(ruleId: string, locale: string, promotion: PrizePromotion | null, now: number): void {
  // Browser only, structurally: `adopt()` runs during SSR for campaign codes,
  // and a page render has no web storage to write to.
  if (!import.meta.client)
    return
  const stored: StoredPrizePromotion = { v: 1, ruleId, locale, at: now, promotion }
  try {
    window.localStorage.setItem(PRIZE_PROMOTION_KEY, JSON.stringify(stored))
  }
  catch {
    // Nothing to fall back to and nothing to repair: without the record every
    // full page load previews the code again — the old cost, not a new one.
  }
}

/**
 * Reads the language the gift copy is worded in, through `$i18n` rather than
 * `useI18n()` for the reason `useUrlPrize` gives: this runs from the coupon
 * plugin, before any component exists.
 *
 * Resolved here, in the composable's synchronous body, and read from the
 * closure later: `adopt()` reads the locale AFTER its round trip, and a
 * `useNuxtApp()` call past an await inside a plugin has lost the instance (the
 * same trap the server half of `plugins/3.urlCoupon` is written around).
 * An unknown locale answers `''`, which matches only a record written under the
 * same unknown — so the two can only ever disagree into a miss, never into the
 * wrong language.
 */
function prizeLocaleReader(): () => string {
  const { $i18n } = useNuxtApp()
  const i18n = $i18n as { locale?: { value?: string } } | undefined
  return () => i18n?.locale?.value ?? ''
}

export function useUrlCoupon() {
  const storedCode = storedCodeCookie()
  const storedPrizeCode = storedPrizeCodeCookie()
  const storedPrizeVerdict = storedPrizeVerdictCookie()
  const prizeLocale = prizeLocaleReader()
  // The validated coupon for this SPA session. Never written without a
  // `couponPreview` round trip having approved the code.
  const coupon = useState<UrlCoupon | null>('urlCoupon', () => null)
  // The validated prize code for this SPA session, with the gift it unlocks.
  const prize = useState<UrlPrize | null>('urlPrize', () => null)

  function forget() {
    storedCode.value = null
    coupon.value = null
  }

  function forgetPrize() {
    storedPrizeCode.value = null
    // The verdict describes a code this browser no longer holds. The gift copy
    // in web storage is left where it is: it is public rule content, it names
    // nobody, and it spares the round trip if a code for the same rule arrives.
    storedPrizeVerdict.value = null
    prize.value = null
  }

  /** The verdict beside `code` records that its mailbox confirmation already got through. */
  function confirmedHere(code: string): boolean {
    const verdict = parsePrizeVerdict(storedPrizeVerdict.value)
    return verdict?.code === code && verdict.confirmed
  }

  /**
   * Confirms the mailbox behind `code` and, once the call got through, records
   * that in the verdict — only while the verdict is still about this code, which
   * a later `adopt()` may have replaced in the meantime.
   */
  function confirmPrize(code: string): void {
    void confirmScratchCode(code).then((delivered) => {
      const verdict = parsePrizeVerdict(storedPrizeVerdict.value)
      if (delivered && verdict?.code === code && !verdict.confirmed)
        storedPrizeVerdict.value = formatPrizeVerdict({ ...verdict, confirmed: true })
    })
  }

  /**
   * Validates `code` and, if it is usable, adopts it — into whichever of the two
   * carriers it belongs to. Codes are upper-cased to match how `applyCouponCode`
   * normalizes them, so the two agree on what is already on the order.
   *
   * Whenever the preview reports a prize, of ANY status and from any origin (the
   * e-mail link, or a restore that could not answer from the verdict beside the
   * code), the mailbox behind it is confirmed unless the verdict records that a
   * confirmation already got through: `emailConfirmedAt` gates the Ecomail push,
   * the SMS export and the 30-day purge of unconfirmed leads, so a single lost
   * call must not cost the lead. The call is idempotent, reveals nothing and is
   * rate-limited server-side.
   */
  async function adopt(code: string): Promise<AdoptOutcome> {
    const normalized = code.trim().toUpperCase()
    if (!normalized)
      return { kind: 'none' }
    if (coupon.value?.code === normalized)
      return { kind: 'coupon', valid: true }
    if (prize.value?.code === normalized)
      return { kind: 'prize', status: 'LIVE' }

    const preview = await previewCoupon(normalized)

    if (preview.prize) {
      const confirmed = confirmedHere(preview.code)
      if (!confirmed)
        confirmPrize(preview.code)
      // A LIVE ledger entry the promotion itself refuses (withdrawn, spent
      // elsewhere) is not something to keep waiting for.
      const status = preview.prize.status === 'LIVE' && !preview.valid ? 'INVALID' : preview.prize.status
      if (status === 'LIVE') {
        const held: UrlPrize = {
          code: preview.code,
          ruleId: preview.prize.ruleId,
          expiresAt: preview.prize.expiresAt,
          promotion: preview.prize.promotion,
        }
        storedPrizeCode.value = held.code
        prize.value = held
        // What lets every later full page load skip this round trip. An expiry
        // we cannot parse is not written at all: a verdict that cannot be
        // re-checked against the clock is worse than no verdict.
        const expiresAt = Date.parse(held.expiresAt)
        if (Number.isFinite(expiresAt)) {
          storedPrizeVerdict.value = formatPrizeVerdict({
            code: held.code,
            ruleId: held.ruleId,
            expiresAt,
            confirmed,
          })
          writePrizePromotion(held.ruleId, prizeLocale(), held.promotion, Date.now())
        }
      }
      else if (storedPrizeCode.value === normalized) {
        forgetPrize()
      }
      return { kind: 'prize', status }
    }

    if (!preview.valid) {
      // A code that is expired, used up or simply wrong must not linger and
      // re-appear on a later cart.
      if (storedCode.value === normalized)
        forget()
      return { kind: 'coupon', valid: false }
    }

    storedCode.value = preview.code
    coupon.value = {
      code: preview.code,
      percent: preview.percent,
      kind: preview.kind,
      excludedFacetValueIds: preview.excludedFacetValueIds,
    }
    return { kind: 'coupon', valid: true }
  }

  /**
   * Puts a prize this browser already holds back into the session — from the
   * verdict stored beside the code, without asking the shop API.
   *
   * `couponPreview` rebuilds the whole prize server-side, and it was being paid
   * for on every FULL page load a code holder made (navigation inside the app
   * always re-used the `useState` prize, so it cost nothing). The verdict it
   * returns is one this browser already has; the parts of it that go off on
   * their own do so on a clock this browser can read, and it re-asks rather
   * than decides. Everything else — redeemed elsewhere, withdrawn — is caught
   * where it decides something: the apply, which validates server-side and
   * already forgets a code the order refuses.
   *
   * Falls back to the full `adopt()` whenever either half is missing — a code
   * adopted before this cache existed, a browser that refuses `localStorage`, a
   * language switched since — so the banner never loses its copy or words it in
   * the wrong language.
   */
  async function restorePrize(code: string): Promise<void> {
    const normalized = code.trim().toUpperCase()
    const verdict = parsePrizeVerdict(storedPrizeVerdict.value)
    if (!verdict || verdict.code !== normalized) {
      await adopt(normalized)
      return
    }

    // The mailbox confirmation, until one gets through (`confirmPrize` records
    // it in the verdict). Once per page life (`confirmScratchCode` dedupes; on
    // the paths below that end in `adopt()` this call is that dedupe's first hit
    // and the one there a no-op). Retried on each load until then because
    // `emailConfirmedAt` gates the Ecomail push, the SMS export and the 30-day
    // purge of unconfirmed leads, and a call lost to a network blip must not
    // leave the lead unconfirmed.
    if (!verdict.confirmed)
      confirmPrize(normalized)

    const now = Date.now()
    const cached = readPrizePromotion(verdict.ruleId, prizeLocale(), now)
    if (!cached) {
      await adopt(normalized)
      return
    }

    // The two verdicts a clock alone turns: the code's own expiry, and the gift
    // rule ending under it — `prizeStatusOf` in the Vendure app reads both the
    // same way, `<= now`, and both are already in hand here. Read to decide
    // whether to ASK, never to decide: the server's clock is the authority, and
    // a device running fast (a phone with a wrong date, a VM resumed from
    // suspend) must not be able to throw away a prize that is still live. The
    // price is one preview for a code past its apparent end, and only one —
    // `adopt()` drops the code for good the moment the server agrees. A rule
    // with no end, or an end that will not parse, reads NaN, and every
    // comparison with NaN is false: that one stays the server's alone.
    const ruleEndsAt = typeof cached.promotion?.endsAt === 'string' ? Date.parse(cached.promotion.endsAt) : Number.NaN
    if (verdict.expiresAt <= now || ruleEndsAt <= now) {
      await adopt(normalized)
      return
    }

    prize.value = {
      code: normalized,
      ruleId: verdict.ruleId,
      expiresAt: new Date(verdict.expiresAt).toISOString(),
      promotion: cached.promotion,
    }
  }

  /**
   * Puts back the codes left over from an earlier visit.
   *
   * The campaign coupon is re-validated rather than trusted — it prices every
   * product surface, so a campaign that has since ended must stop advertising a
   * discount the cart will refuse. The prize code is not: it discounts nothing,
   * and `restorePrize` above answers it from what this browser already knows.
   *
   * `campaignOnly` is for the server half of the plugin: restoring a prize code
   * confirms the mailbox behind it, which is a MUTATION, and a page render has
   * no business sending one. The browser restores the prize a moment later.
   */
  async function restore(options: { campaignOnly?: boolean } = {}): Promise<void> {
    await Promise.all([
      !coupon.value && storedCode.value ? adopt(storedCode.value) : null,
      !options.campaignOnly && !prize.value && storedPrizeCode.value ? restorePrize(storedPrizeCode.value) : null,
    ])
  }

  // `storedCode` is exposed as well as used: a server render has to know it was
  // serving a campaign visitor even when the code turns out to be dead, because
  // that render still carries the `Set-Cookie` that clears it.
  return { coupon, storedCode, prize, adopt, restore, forget, forgetPrize }
}

/**
 * The product price with the campaign coupon deducted, or `null` when no
 * per-product figure can be shown. An order-percentage promotion prorates over
 * every line, so a single unit ends up exactly `percent` cheaper.
 */
export function couponPrice(price: number, percent: number): number {
  return Math.round(price * (100 - percent) / 100)
}

/**
 * The badge text for an active campaign coupon — "-30 % · BACK30". One
 * definition, so the product page, the listing cards and the search tiles
 * cannot word the same offer three different ways, and one width: the copy is
 * already as short as it can be while naming both the depth and the code, so
 * there is nothing for a wider screen to spend space on. Empty when no coupon
 * applies.
 *
 * About the offer, not about one product: a surface that shows the badge against
 * a particular product takes it from `couponCardPricing`, which returns nothing
 * for a product the coupon steps around.
 */
export function useCouponBadge() {
  const { coupon } = useUrlCoupon()
  const { t } = useI18n()

  return computed(() => {
    const active = coupon.value
    return active?.percent
      ? t('coupon_code.extra_discount_badge', { percent: active.percent, code: active.code })
      : ''
  })
}

/** What a campaign coupon changes about a product's displayed price. */
export interface CouponCardPricing {
  /** The price with the coupon deducted — what the cart will charge. */
  price: string
  /** That same figure as a raw amount, for callers doing their own arithmetic. */
  amount: number
  /**
   * The code itself. Surfaces render it through
   * `coupon_code.price_with_code` — "1 063 Kč s kódem: EXTRA" — rather than
   * taking a finished string from here, because the amount is set apart from
   * the words around it. Naming the code is what keeps that lower figure from
   * reading as a second permanent price cut.
   */
  code: string
  /**
   * The pre-coupon price, for products that carry no sale of their own. A sale
   * product keeps its own pre-sale price struck through instead, so that the
   * discount it already had stays visible next to the campaign's.
   */
  priceBeforeCoupon: string
  /** Badge text naming the offer: "-30 % · BACK30". */
  badge: string
  /** Everything the coupon saves against `comparedTo`, formatted: "686 Kč". */
  saved: string
  /** That saving worded as the sale badge would word it: "Sleva 686 Kč". */
  savedBadge: string
}

/**
 * What a campaign coupon does to a product's price wherever that product is
 * listed. The coupon discounts the whole order, so it applies to every product
 * on every surface, and it stacks on top of a sale rather than replacing it:
 * the sale already cut the price, the coupon cuts what is left. Both are
 * therefore reported separately — the caller keeps its own sale badge and adds
 * this one beside it.
 *
 * Returns `null` when no per-product coupon discount applies, which is the
 * signal for callers to show their existing pricing untouched.
 */
export function useCouponCardPricing() {
  const { coupon } = useUrlCoupon()
  const priceFormatter = usePriceFormatter()
  const couponBadge = useCouponBadge()
  const savedAmountBadge = useSavedAmountBadge()

  /**
   * `comparedTo` is the price the shopper would otherwise pay — the pre-sale
   * price for a discounted product, so `saved` is everything the sale and the
   * code save together. Omit it and the saving is measured against `amount`,
   * which is all a product without a sale of its own has to be measured against.
   *
   * `product` is whatever the caller holds that carries facet values — a search
   * result, a product, an order line's product. Pass it and a gift card prices
   * at its catalogue price, badge and all, exactly as the cart will charge for
   * it; omit it only where the thing being priced cannot be one.
   */
  function couponCardPricing(
    amount?: number | null,
    currency?: CurrencyCode | null,
    comparedTo?: number | null,
    product?: CouponFacetValueSource | null,
  ): CouponCardPricing | null {
    const active = coupon.value
    const badge = couponBadge.value
    if (!active?.percent || !badge || !amount || !currency)
      return null
    if (couponExcludes(active, product))
      return null

    const discounted = couponPrice(amount, active.percent)
    const price = priceFormatter(discounted, currency)
    const saved = Math.max((comparedTo ?? amount) - discounted, 0)

    return {
      price,
      amount: discounted,
      code: active.code,
      priceBeforeCoupon: priceFormatter(amount, currency),
      badge,
      saved: priceFormatter(saved, currency),
      savedBadge: savedAmountBadge(saved, currency) ?? '',
    }
  }

  return { couponCardPricing }
}
